// Icons

import ChannelsClassicIcon from '@components/atoms/icons/channelsClassic';
import ChannelGuideIcon from '@components/atoms/icons/channelGuide';
import ChannelsIcon from '@components/atoms/icons/channels';
import InformationIcon from '@components/atoms/icons/information';
import LanguageIcon from '@components/atoms/icons/language';
import L7DIcon from '@components/atoms/icons/l7d';
import PlayIcon from '@components/atoms/icons/play';
import RecordCancelIcon from '@components/atoms/icons/recordCancel';
import RecordDeleteIcon from '@components/atoms/icons/recordDelete';
import RecordStartIcon from '@components/atoms/icons/recordStart';
import RestartIcon from '@components/atoms/icons/restart';
import StopWatchingIcon from '@components/atoms/icons/stopWatching';
import WatchEverythingIcon from '@components/atoms/icons/watchEverything';
import WatchLaterIcon from '@components/atoms/icons/watchLater';
// ---

import AdUtils from '@shared/utils/ads';
import AnalyticsInstance, { AnalyticEventType } from './analytics';
import ChannelUtils from '@shared/utils/channels';
import ChannelView from '@shared/types/enums/channelView';
import ContainerType from '@shared/types/enums/container';
import Content from '@shared/types/enums/content';
import Event from '@shared/classes/event';
import FastEvent from '@shared/classes/fastEvent';
import Features from '@shared/types/enums/features';
import FlavourConfig from '@shared/config/flavours';
import MediaProduction from '@shared/classes/mediaProduction';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import Plan from '@shared/classes/plan';
import PlanUtils from '@shared/utils/plan';
import Recording from '@shared/types/enums/recording';
import RecordType from '@shared/types/enums/recordType';
import Routes from '@shared/types/enums/routes';
import Sections from '@shared/types/enums/sections';
import Services from '@shared/types/enums/services';
import Storage from '@storage';
import addKeepWatching from '@shared/api/keepWatching/addKeepWatchingElement';
import goToChannelGuide from '@shared/actions/channels/goToChannelGuide';
import goToChannelList from '@shared/actions/channels/goToChannelList';
import goToChannelMain from '@shared/actions/channels/goToChannelMain';
import goToClassicChannel from '@shared/actions/channels/goToClassicChannel';
import goToContent from '@shared/actions/content/goToContent';
import goToDetail from '@shared/actions/detail/goToDetail';
import goToWatchEverything from '@shared/actions/routes/goToWatchEverything';
import goToL7D from '@shared/actions/routes/goToL7D';
import removeKeepWatching from '@shared/api/keepWatching/removeKeepWatchingElement';
import { FloatingMenuButton } from '@shared/classes/floatingMenu';
import { RailType } from '@shared/types/enums/rail';
import { addRecordingRequest } from '@shared/api/recording/recordingsRequest';
import { clearPauseAd, setAds } from '@shared/store/slices/ads.slice';
import { closeFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { play, setPosition } from '@shared/store/slices/media.slice';
import { reload } from '@shared/store/slices/floatingMenu.slice';
import { setCurrentRecording } from '@shared/store/slices/recordings.slice';
import { setParams, updateParams } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import {
  clearModal,
  setModal,
  setModalParams,
} from '@shared/store/slices/modal.slice';
import {
  addKeepWatchingItem,
  removeKeepWatchingItem,
} from '@shared/store/slices/keepWatching.slice';

/**
 * Utility object containing common actions for floating menu operations.
 */
const Actions = {
  closeMenu: () => {
    store.dispatch(closeFloatingMenu());
  },
  reloadMenu: () => {
    store.dispatch(reload());
  },
  triggerAd: (section?: Sections) => {
    if (section) {
      const adSource = AdUtils.getSource(section);
      if (adSource) {
        store.dispatch(setAds(adSource));
        return true;
      }
    }
    return false;
  },
};

/**
 * Collection of predefined floating menu button configurations.
 * Each button defines an action, icon, and text label for menu interactions.
 */
const Buttons = {
  /**
   * Quick access buttons
   */
  CLASSIC_CHANNEL: new FloatingMenuButton({
    text: 'ACTION.CLASSIC_CHANNEL',
    action: (asset?: Event | null) => {
      if (asset) {
        Actions.closeMenu();
        Storage.set('userChannelViewSelection', `${ChannelView.Classic}`);
        goToClassicChannel(asset.channel);
      }
    },
    icon: ChannelsClassicIcon,
  }),
  DETAIL: new FloatingMenuButton({
    text: 'ACTION.DETAIL',
    action: (asset?: Event | null) => {
      Actions.closeMenu();
      if (asset) {
        goToDetail(asset as FastEvent);
      }
    },
    icon: InformationIcon,
  }),
  GUIDE: new FloatingMenuButton({
    text: 'ACTION.GUIDE',
    action: (asset?: Event | null) => {
      Actions.closeMenu();
      Actions.triggerAd(Sections.GUIDE_TV);
      goToChannelGuide(asset);
    },
    icon: ChannelGuideIcon,
  }),
  LAST7DAYS: new FloatingMenuButton({
    text: FlavourConfig.capabilities.u7d ? 'ACTION.U7D' : 'ACTION.WATCH_MORE',
    action: (asset?: Event | null) => {
      // Get ad depending on the page the user triggers the menu from
      const { router } = store.getState();
      let section = undefined;
      switch (router.route.Page) {
        case Pages.CHANNELS_MAIN:
        case Pages.INITIAL:
        case Pages.RECORDINGS:
          section = Sections.CHANNEL_U7D;
          break;
        case Pages.MOVIES:
          section = Sections.MOVIES_U7D;
          break;
        case Pages.SERIES_U7D:
          section = Sections.SERIES_U7D;
          break;
        case Pages.SHOWS_U7D:
          section = Sections.SHOWS_U7D;
          break;
      }

      // Route to new page and dispatch ads
      Actions.closeMenu();
      Actions.triggerAd(section);
      goToL7D(asset);
    },
    icon: FlavourConfig.capabilities.u7d ? L7DIcon : WatchEverythingIcon,
  }),
  TV_CHANNELS: new FloatingMenuButton({
    text: 'ACTION.CHANNELS',
    action: (asset?: Event | null) => {
      Actions.closeMenu();
      const { router } = store.getState();
      if (router.route.Page === Pages.CLASSIC_CHANNEL) {
        Storage.set('userChannelViewSelection', `${ChannelView.Dial}`);
        goToChannelMain(asset?.channel);
      } else {
        goToChannelList();
      }
    },
    icon: ChannelsIcon,
  }),
  /**
   * Player buttons
   */
  PLAY: new FloatingMenuButton({
    text: 'ACTION.PLAY',
    action: (asset?: Event | null) => {
      if (asset) {
        Actions.closeMenu();
        goToContent(asset, false, asset.mocked);
      }
    },
    icon: PlayIcon,
  }),
  PLAY_LIVE: new FloatingMenuButton({
    text: 'ACTION.WATCH_NOW',
    action: (asset?: Event | null) => {
      if (asset) {
        Actions.closeMenu();
        goToContent(asset, false, asset.mocked);
      }
    },
    icon: PlayIcon,
  }),
  LANGUAGES: new FloatingMenuButton({
    text: 'ACTION.LANGUAGES',
    action: (_asset?: Event | null, node?: number | null) => {
      Actions.closeMenu();
      store.dispatch(setModalParams({ previousNode: node, event: _asset }));
      store.dispatch(setModal(Modals.PLAYER_LANGUAGE));
    },
    icon: LanguageIcon,
  }),
  /**
   * Record buttons
   */
  RECORD: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      if (asset && !asset.serieId) {
        
        addRecordingRequest(asset.eventId, RecordType.SHOW).then(() =>
          store.dispatch(reload())
        ).catch((e) => {
          if(e.error_code == '201') {
            store.dispatch(setModal(Modals.UPGRADE_RECORD_PLAN));
          }
        });
        Actions.closeMenu();
        return;
      }
      Actions.closeMenu();
      store.dispatch(setModalParams({ asset, previousNode: node }));
      store.dispatch(setModal(Modals.RECORDING_REQUEST));
    },
    icon: RecordStartIcon,
  }),
  RECORD_UPGRADE_PLAN: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      Actions.closeMenu();
      const upgradablePlan = ChannelUtils.getNextUpgradablePlan(
        asset?.channel,
        true
      );
      store.dispatch(
        setModalParams({
          channel: asset?.channel,
          plan: upgradablePlan,
          previousNode: node,
        })
      );
      store.dispatch(setModal(Modals.UPGRADE_PLAN));
    },
    icon: RecordStartIcon,
  }),
  RECORD_UPGRADE_PLAN_CHANNEL_FORBBIDEN: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      Actions.closeMenu();
      const upgradablePlan = ChannelUtils.getUpgradablePlan(
        asset?.channel,
        true
      );
      store.dispatch(
        setModalParams({
          channel: asset?.channel,
          plan: upgradablePlan,
          previousNode: node,
        })
      );
      store.dispatch(setModal(Modals.UPGRADE_PLAN));
    },
    icon: RecordStartIcon,
  }),
  CANCEL_RECORD: new FloatingMenuButton({
    text: 'ACTION.CANCEL_RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      if (asset) {
        const { recordings } = store.getState();
        const identifier = recordings.identifiers.find((item) => {
          return item.code === asset?.eventId;
        });
        store.dispatch(setCurrentRecording({ ...identifier }));
        Actions.closeMenu();
        store.dispatch(
          setModalParams({
            asset,
            previousNode: node,
            recordType: Recording.Pending,
          })
        );
        store.dispatch(setModal(Modals.RECORDING_DELETE));
      }
    },
    icon: RecordCancelIcon,
  }),
  DELETE_RECORD: new FloatingMenuButton({
    text: 'ACTION.DELETE_RECORD',
    action: (asset?: Event | null, node?: number | null) => {
      if (asset) {
        const { recordings } = store.getState();
        const identifier = recordings.identifiers.find((item) => {
          return item.code === asset?.eventId;
        });
        store.dispatch(setCurrentRecording({ ...identifier }));
        Actions.closeMenu();
        store.dispatch(
          setModalParams({
            asset,
            previousNode: node,
            recordType: Recording.Finished,
          })
        );
        store.dispatch(setModal(Modals.RECORDING_DELETE));
      }
    },
    icon: RecordDeleteIcon,
  }),
  NOT_ALLOWED_RECORD_ANONYMOUS: new FloatingMenuButton({
    text: 'ACTION.RECORD',
    action: (_asset?: Event | null, node?: number | null) => {
      Actions.closeMenu();
      store.dispatch(setModalParams({ previousNode: node }));
      store.dispatch(setModal(Modals.ANONYMOUS_GENERIC));
    },
    icon: RecordStartIcon,
  }),
  /**
   * Replay buttons
   */
  RESTART_CONTENT: {
    text: 'ACTION.REPLAY',
    action: (asset?: Event | null) => {
      Actions.closeMenu();
      goToContent(asset, true);
    },
    icon: RestartIcon,
  },
  RESTART_MEDIA: {
    text: 'ACTION.REPLAY',
    action: (asset?: Event | null) => {
      Actions.closeMenu();
      store.dispatch(
        setPosition({
          position: (asset as FastEvent).startBookmark,
          seek: true,
        })
      );
      setTimeout(() => {
        store.dispatch(play());
      });
    },
    icon: RestartIcon,
  },
  RESTART_PLAYER: new FloatingMenuButton({
    text: 'ACTION.REPLAY',
    action: (asset?: Event | null) => {
      Actions.closeMenu();
      store.dispatch(
        setPosition({ position: (asset as FastEvent)?.start || 0, seek: true })
      );
    },
    icon: RestartIcon,
  }),
  STARTOVER: new FloatingMenuButton({
    text: 'ACTION.REPLAY',
    action: (_asset?: Event | null, node?: number | null) => {
      Actions.closeMenu();
      AnalyticsInstance.addEvent(AnalyticEventType.RESTART);
      Actions.triggerAd(Sections.RESTART_LIVE);
      store.dispatch(
        setParams({
          position: 0,
          restart: true,
        })
      );
      store.dispatch(clearModal());
      store.dispatch(clearPauseAd());
      setTimeout(() => {
        store.dispatch(play());
      }, 1000);
    },
    icon: RestartIcon,
  }),
  /**
   * Watch everything buttons
   */
  WATCH_EVERYTHING: new FloatingMenuButton({
    text: 'ACTION.SEE_ALL',
    action: (asset?: Event | null) => {
      if (asset) {
        Actions.closeMenu();
        goToWatchEverything(asset);
      }
    },
    icon: WatchEverythingIcon,
  }),
  STOP_WATCHING: new FloatingMenuButton({
    text: 'ACTION.STOP_WATCHING',
    action: (asset?: Event | null) => {
      if (asset?.eventId) {
        store.dispatch(removeKeepWatchingItem(asset.eventId));
        store.dispatch(updateParams({ updateKeepWatching: true }));
        Actions.closeMenu();
        removeKeepWatching(asset.eventId);
      }
    },
    icon: StopWatchingIcon,
  }),
  /**
   * Watch later buttons
   */
  WATCH_LATER: new FloatingMenuButton({
    text: 'ACTION.WATCH_LATER',
    action: (asset?: Event | null) => {
      if (asset?.eventId) {
        store.dispatch(addKeepWatchingItem({ id: asset.eventId, mark: 0 }));
        Actions.closeMenu();
        addKeepWatching(asset.eventId);
      }
    },
    icon: WatchLaterIcon,
  }),
  WATCH_LATER_ANONYMOUS: new FloatingMenuButton({
    text: 'ACTION.WATCH_LATER',
    action: (_asset?: Event | null, node?: number | null) => {
      Actions.closeMenu();
      store.dispatch(setModalParams({ previousNode: node }));
      store.dispatch(setModal(Modals.ANONYMOUS_GENERIC));
    },
    icon: WatchLaterIcon,
  }),
  WATCH_LATER_UPGRADABLE_PLAN: new FloatingMenuButton({
    text: 'ACTION.WATCH_LATER',
    action: (asset?: Event | null, node?: number | null) => {
      Actions.closeMenu();
      const upgradablePlan = ChannelUtils.getUpgradablePlan(asset?.channel);
      store.dispatch(
        setModalParams({
          channel: asset?.channel,
          plan: upgradablePlan,
          previousNode: node,
        })
      );
      store.dispatch(setModal(Modals.UPGRADE_PLAN));
    },
    icon: WatchLaterIcon,
  }),
};

/**
 * Adds the classic channel view button to the menu if on the channels main page.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} _asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getClassicViewButton(
  _route: number,
  _asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  if (router.route.Page === Pages.CHANNELS_MAIN) {
    buttons.push(Buttons.CLASSIC_CHANNEL);
  }
}

/**
 * Adds the main action button (channels or play) based on route and asset.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getMainButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  if (route === Routes.ZAPPER || router.route.Page === Pages.CLASSIC_CHANNEL) {
    const button = Buttons.TV_CHANNELS;
    if (router.route.Page === Pages.CLASSIC_CHANNEL) {
      button.text = 'ACTION.DIAL_VIEW';
    }
    buttons.push(button);
  } else if (
    asset &&
    (route === Routes.MAIN ||
      route === Routes.DETAIL ||
      route === Routes.CHANNEL_LIST)
  ) {
    const upgradablePlan = ChannelUtils.getUpgradablePlan(asset.channel);
    if (upgradablePlan) return;
    if (
      asset.type === Content.EPG_EVENT &&
      !asset.isCurrentEvent() &&
      !asset.mocked
    ) {
      return;
    }
    if (
      (asset as MediaProduction).type === Content.MEDIA_PRODUCTION &&
      !(asset as MediaProduction).streams
    ) {
      return;
    }
    if (asset.type === Content.EPG_EVENT) {
      buttons.push(Buttons.PLAY_LIVE);
    } else {
      buttons.push(Buttons.PLAY);
    }
  }
}

/**
 * Adds restart/replay button based on content type and playback state.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getRestartButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { media } = store.getState();
  const planToEvaluate = PlanUtils.checkFeature(Features.LAST_SHOWS, [
    ...(asset?.channel?.extraPlans?.map((plan) => {
      return plan.id;
    }) || []),
  ]);
  const channelServices = asset?.channel?.services || [];
  if (asset?.mocked) return;
  // Currently in main, channel list, detail or player, allow restart content or restart media
  if (
    asset &&
    (route === Routes.MAIN ||
      route === Routes.CHANNEL_LIST ||
      route === Routes.DETAIL ||
      route === Routes.PLAYER)
  ) {
    if (
      asset.type === Content.MEDIA_PRODUCTION ||
      asset.type === Content.SHOW
    ) {
      // Get keep watching value from state or event
      const { keepWatching } = store.getState();
      const keepWatchingValue =
        keepWatching.data.find((item) => {
          return item.id === asset.eventId;
        })?.mark || (asset as FastEvent).keepWatchingMark;

      // If keep watching value is found, add restart media button
      if (route !== Routes.PLAYER) {
        if (keepWatchingValue) {
          buttons.push(Buttons.RESTART_CONTENT);
        }
      } else {
        buttons.push(Buttons.RESTART_MEDIA);
      }
    } else if (asset.type === Content.EPG_EVENT) {
      if (
        asset.isCurrentEvent() &&
        channelServices?.includes(Services.TIMESHIFT) &&
        planToEvaluate
      ) {
        buttons.push(Buttons.RESTART_CONTENT);
      }
    }
  }
  // Currently in player, allow restart player
  else if (route === Routes.PLAYER && media.timeshift) {
    buttons.push(Buttons.RESTART_PLAYER);
  }
  // Currently in zapper, allow startover if timeshift is enabled and plan is evaluated
  else if (route === Routes.ZAPPER && media.timeshift) {
    if (planToEvaluate && channelServices?.includes(Services.TIMESHIFT)) {
      buttons.push(Buttons.STARTOVER);
    }
  }
}

/**
 * Adds recording-related buttons based on channel services and plan features.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getRecordButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  const isSeasonRecorded =
    router?.params?.current?.isSeason && router?.params?.current?.isRecord;
  if (
    !asset?.channel?.services?.includes(Services.RECORDINGS) &&
    !asset?.channel?.services?.includes(Services.RECORDINGS_FREE) &&
    !isSeasonRecorded
  ) {
    return;
  }
  if (route === Routes.PLAYER) return;
  const { user } = store.getState();
  const planToEvaluate = PlanUtils.checkFeature(Features.ALLOW_RECORD, [
    ...(asset?.channel?.extraPlans.map((plan) => {
      return plan.id;
    }) || []),
  ]);
  const upgradablePlan = ChannelUtils.getUpgradablePlan(asset?.channel);

  // Nested functions

  /**
   * Checks if the current event is already being recorded.
   *
   * @returns {Object | undefined} Recording type if found, undefined otherwise
   */
  const checkRecording = () => {
    const { recordings } = store.getState();
    let recordingFound = recordings.identifiers.find((item) => {
      return item.code === asset?.eventId;
    });
    if (recordingFound) {
      const isPendingEvent = asset?.isFutureEvent() || asset?.isCurrentEvent();
      return { type: isPendingEvent ? Recording.Pending : Recording.Finished };
    }
  };

  /**
   * Determines and adds the appropriate record button based on recording state.
   */
  const getButton = () => {
    const check = checkRecording();
    if (check?.type === Recording.Pending) {
      buttons.push(Buttons.CANCEL_RECORD);
    } else if (check?.type === Recording.Finished || isSeasonRecorded) {
      buttons.push(Buttons.DELETE_RECORD);
    }
    if (!check) {
      if (upgradablePlan) {
        buttons.push(Buttons.RECORD_UPGRADE_PLAN_CHANNEL_FORBBIDEN);
      } else if (!asset?.mocked && asset?.type === Content.EPG_EVENT) {
        buttons.push(Buttons.RECORD);
      }
    }
  };

  // Record button logic
  if (
    planToEvaluate &&
    !(asset?.rail && asset?.rail === RailType.KEEP_WATCHING)
  ) {
    getButton();
  } else if (asset?.type === Content.EPG_EVENT) {
    if (
      (asset?.channel?.services?.includes(Services.RECORDINGS) &&
        !(planToEvaluate as Plan)?.isFree) ||
      asset?.channel?.services?.includes(Services.RECORDINGS_FREE)
    ) {
      if (planToEvaluate) {
        getButton();
      } else {
        if (user.isAnonymous) {
          buttons.push(Buttons.NOT_ALLOWED_RECORD_ANONYMOUS);
        } else if (!planToEvaluate) {
          if (
            (asset?.channel?.services?.includes(Services.RECORDINGS) ||
              asset?.channel?.services?.includes(Services.RECORDINGS_FREE)) &&
            ChannelUtils.getNextUpgradablePlan(asset.channel, true)
          )
            buttons.push(Buttons.RECORD_UPGRADE_PLAN);
        }
      }
    } else if (asset?.channel?.services?.includes(Services.RECORDINGS)) {
      if (ChannelUtils.getNextUpgradablePlan(asset.channel, true)) {
        buttons.push(Buttons.RECORD_UPGRADE_PLAN);
      }
    }
  }
}

/**
 * Adds detail button if asset is available and not mocked.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getDetailButton(
  route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  if (asset && !asset.mocked && route !== Routes.DETAIL) {
    buttons.push(Buttons.DETAIL);
  }
}

/**
 * Adds language selection button if multiple audio/subtitle tracks are available.
 *
 * @param {number} route - Current route identifier
 * @param {Event | null | undefined} _asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getLanguageButton(
  route: number,
  _asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { media } = store.getState();
  if (
    [Routes.PLAYER, Routes.ZAPPER].includes(route) &&
    (media.tracks.audio.length > 1 || media.tracks.subtitles.length > 1)
  ) {
    buttons.push(Buttons.LANGUAGES);
  }
}

/**
 * Adds last 7 days (L7D) button if feature is enabled and channel supports it.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getL7DButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { router } = store.getState();
  const planToEvaluate = PlanUtils.checkFeature(Features.LAST_SHOWS, [
    ...(asset?.channel?.extraPlans.map((plan) => {
      return plan.id;
    }) || []),
  ]);
  if (
    planToEvaluate &&
    asset &&
    !asset.channel?.isFast &&
    asset.channel?.id &&
    (asset.channel.services.includes(Services.YOU_MISSED_IT) ||
      asset.channel.services.includes(Services.YOU_MISSED_IT_FREE)) &&
    router.route.Page !== Pages.LAST_SHOWS_CHANNEL
  ) {
    buttons.push(Buttons.LAST7DAYS);
  }
}

/**
 * Adds channel guide button if event has date information.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getChannelGuideButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  if (asset?.date?.start && asset?.date?.end && asset?.channel) {
    const {
      router: {
        route: { Page },
      },
    } = store.getState();
    if (Page !== Pages.CHANNEL_GUIDE && Page !== Pages.PLAYER) {
      buttons.push(Buttons.GUIDE);
    }
  }
}

/**
 * Adds watch everything button for FAST channels.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getWatchEverythingButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  if (
    asset?.channel?.isFast &&
    asset.channel.type !== Content.AVOD &&
    asset.channel.type !== Content.SVOD
  ) {
    const { router } = store.getState();
    if (router.route.Page !== Pages.WATCH_EVERYTHING) {
      buttons.push(Buttons.WATCH_EVERYTHING);
    }
  }
}

/**
 * Adds watch later/keep watching button based on user state and content type.
 *
 * @param {number} _route - Current route identifier
 * @param {Event | null | undefined} asset - Current event asset
 * @param {FloatingMenuButton[]} buttons - Array to add button to
 */
function getWatchLaterButton(
  _route: number,
  asset: Event | null | undefined,
  buttons: FloatingMenuButton[]
) {
  const { keepWatching, user } = store.getState();
  if (
    asset &&
    (asset?.type === Content.MEDIA_PRODUCTION ||
      asset?.type === Content.SHOW) &&
    (asset as MediaProduction).contentContainerType !== ContainerType.PARENT
  ) {
    if (user.isAnonymous) {
      buttons.push(Buttons.WATCH_LATER_ANONYMOUS);
    } else {
      const isKeepWatchingEvent = keepWatching.data.find((item) => {
        return item.id === asset.eventId;
      });
      if (isKeepWatchingEvent) {
        buttons.push(Buttons.STOP_WATCHING);
      } else {
        const upgradablePlan = ChannelUtils.getUpgradablePlan(asset.channel);
        if (upgradablePlan) {
          buttons.push(Buttons.WATCH_LATER_UPGRADABLE_PLAN);
        } else {
          buttons.push(Buttons.WATCH_LATER);
        }
      }
    }
  }
}

const Coordinates = {
  CHANNEL_LIST: { x: 884, y: 168 },
  DETAIL: { x: 400, y: 386 },
  MAIN: { x: 462, y: 230 },
  MOSAIC: { x: 460, y: 256 },
  MOSAIC_RIGHT_BORDER: { x: 1546, y: 256 },
  POSTER_RAIL: { x: 312, y: 128 },
  RELATED_RAIL: { x: 300, y: 230 },
  SQUARE_RAIL: { x: 224, y: 312 },
  ZAPPER: { x: 280, y: 310 },
  PAUSE_ADS: { x: 370, y: 330 },
};

/**
 * Utility object containing helper functions for floating menu operations.
 */
const Utils = {
  /**
   * Generates the list of floating menu buttons based on the current route and asset.
   *
   * @param {Event | null | undefined} asset - Current event asset
   * @param {number} route - Current route identifier
   * @returns {FloatingMenuButton[]} Array of configured menu buttons
   */
  getMenuButtons(asset: Event | null | undefined, route: number) {
    const buttons: FloatingMenuButton[] = [];
    getClassicViewButton(route, asset, buttons);
    getMainButton(route, asset, buttons);
    getRestartButton(route, asset, buttons);
    getRecordButton(route, asset, buttons);
    getDetailButton(route, asset, buttons);
    getL7DButton(route, asset, buttons);
    getChannelGuideButton(route, asset, buttons);
    getWatchEverythingButton(route, asset, buttons);
    getWatchLaterButton(route, asset, buttons);
    getLanguageButton(route, asset, buttons);
    return buttons;
  },
};

export { Coordinates, Utils as FloatingMenuUtils };
